import { useTranslation } from 'react-i18next';
import { useAppSelector, usePlatform } from '../redux-file/hooks';
import { RootState } from '../redux-file/store';
import {
  OrganizationDataOutSchema,
  ProductPlatform,
} from '../utils/api.interfaces';
import { RequiredStateError } from './exceptions';
import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { Country, IsoCountry } from './types';
import { client } from 'src/utils/api-client';
import en from 'date-fns/locale/en-US';
import pl from 'date-fns/locale/pl';
import cs from 'date-fns/locale/cs';
import de from 'date-fns/locale/de';
import ro from 'date-fns/locale/ro';
import { defaultElementsPerPageOptions } from 'src/components/Pagination';
import React from 'react';
import { UUID } from 'crypto';
import { toast } from 'react-toastify';
import i18next from 'i18next';

export const useRequiredSelector = <TProp>(
  // eslint-disable-next-line no-unused-vars
  selector: (state: RootState) => TProp | undefined
) =>
  useAppSelector((state) => {
    const result = selector(state);
    if (result === undefined) {
      throw new RequiredStateError();
    }
    return result;
  });

function assertPropertyRequired<T, K extends keyof T>(
  obj: T,
  key: K
): asserts obj is T & Record<K, NonNullable<T[K]>> {
  if (obj[key] === undefined || obj[key] === null) {
    throw new RequiredStateError();
  }
}

export const useOrganization = () => {
  const { activeOrganization } = usePlatform();
  if (activeOrganization === null) throw new RequiredStateError();
  return activeOrganization;
};

export const useProductOrganization = <T extends ProductPlatform>(
  product: T
): OrganizationDataOutSchema &
  Record<T, NonNullable<OrganizationDataOutSchema[T]>> => {
  const organization = useOrganization();
  assertPropertyRequired(organization, product);
  return organization;
};

export const useGhgOrganization = () =>
  useProductOrganization(ProductPlatform.Co2);
export const useEsgOrganization = () =>
  useProductOrganization(ProductPlatform.Esg);
export const useLcaOrganization = () =>
  useProductOrganization(ProductPlatform.Lca);
export const useCbamOrganization = () =>
  useProductOrganization(ProductPlatform.Cbam);

const getLocale = (language: string) => {
  switch (language) {
    case 'cs':
      return cs;
    case 'de':
      return de;
    case 'pl':
      return pl;
    case 'ro':
      return ro;
    case 'en':
      return en;
    default:
      return en;
  }
};

export type UseLanguageOptions = {
  keyPrefix?: string;
};

export const useLanguage = (options?: UseLanguageOptions) => {
  const { t, i18n } = useTranslation(undefined, {
    keyPrefix: options?.keyPrefix,
  });
  const locale = getLocale(i18n.language);
  return {
    t,
    i18n,
    language: i18n.language,
    setLanguage: i18n.changeLanguage,
    locale,
  };
};

export const useContactUsPrompt = () => {
  return {
    navigateMailTo: () => window.open('mailto:contact@envirly.com', '_blank'),
  };
};

export const useSupportedCountries = () => {
  const { language } = useLanguage();
  const query = useQuery<Country[]>(['supportedCountries', { language }], () =>
    client
      .get('web/countries/supported-countries')
      .then((res) => res?.data || [])
  );
  return { ...query, data: query.data || [] };
};

export const useAllCountries = () => {
  const { language } = useLanguage();
  const query = useQuery<IsoCountry[]>(['allCountries', { language }], () =>
    client.get('web/countries/all').then((res) => res?.data || [])
  );
  return { ...query, data: query.data || [] };
};

export const useEUCountries = () => {
  const { language } = useLanguage();
  const query = useQuery<IsoCountry[]>(['EuCountries', { language }], () =>
    client.get('web/countries/eu').then((res) => res?.data || [])
  );
  return { ...query, data: query.data || [] };
};

export const useNonEUCountries = () => {
  const { language } = useLanguage();
  const query = useQuery<IsoCountry[]>(['NonEuCountries', { language }], () =>
    client.get('web/countries/non-eu').then((res) => res?.data || [])
  );
  return { ...query, data: query.data || [] };
};

export type QueryOptions<OutSchema> = Omit<
  UseQueryOptions<OutSchema, Error, OutSchema, QueryKey>,
  'queryKey' | 'queryFn'
>;

export const useResourceController = <
  InSchema,
  OutSchema extends { id: number | UUID },
>(
  url: string,
  queryKey: any[],
  // when params has { id } it is considered as single resource request
  params: { [key: string]: any } = {},
  queryOptions: QueryOptions<any> = {
    // IMPORTANT: by default query will be disabled if any of the params is null or undefined
    // eg { id: null } or {name: undefined} etc.
    // Since we are using hooks we want to disable the query until all required params are provided
    // otherwise the request will be sent twice: firslty for all request.user data, then for the queryParam data
    enabled: !Object.values(params).some(
      (value) => value === null || value === undefined
    ),
  }
) => {
  const queryClient = useQueryClient();

  // when params has { id } it is considered as single resource request
  const isSingleResourceRequest = params.id !== undefined;

  // disable query if any of the params is null/undefined or if it's detailed request { id }
  const getQueryOptions = (isSingleResourceQuery: boolean = false) => ({
    ...queryOptions,
    enabled:
      queryOptions.enabled && isSingleResourceQuery === isSingleResourceRequest,
  });

  const onSuccess = () => {
    // TODO: consider something more fancy
    toast.success(i18next.t('common.success'));
    // always invalidate full (first item) query after success
    queryClient.invalidateQueries(queryKey[0]);
  };
  const onError = (error: Error) => {
    toast.error(String(error));
  };

  // query object for list of OutSchema
  const query = useQuery<OutSchema[], Error>(
    queryKey,
    () => client.get<OutSchema[]>(url, { params }).then((res) => res.data),
    getQueryOptions(false)
  );

  // query object for single OutSchema
  const singleQuery = useQuery<OutSchema, Error>(
    queryKey,
    () => client.get<OutSchema>(`${url}/${params.id}`).then((res) => res.data),
    getQueryOptions(true)
  );

  // POST, PUT, DELETE
  const create = useMutation({
    mutationFn: (data: Partial<InSchema>) =>
      client.post(url, data).then((res) => res.data),
    onSuccess,
    onError,
  });
  const update = useMutation({
    mutationFn: (data: Partial<OutSchema>) =>
      client.put(`${url}/${data.id}`, data).then((res) => res.data),
    onSuccess,
    onError,
  });
  const _delete = useMutation({
    mutationFn: (id: number) => client.delete(`${url}/${id}`),
    onSuccess,
    onError,
  });

  // list of OutSchema (initially empty)
  const _data = query.data || [];

  // map of OutSchema where key is id and value is OutSchema
  const _dataMap = !isSingleResourceRequest
    ? _data.reduce((acc: Record<number | UUID, OutSchema>, item) => {
        acc[item.id] = item;
        return acc;
      }, {})
    : {};

  // OutSchema, singleQuery.data or undefined
  const _instance: OutSchema | undefined = singleQuery.data;

  // default query object where query.data is undefined (till request success) or list or single of OutSchema
  const _query = isSingleResourceRequest ? singleQuery : query;

  return {
    ..._query, // default query object where query.data is undefined (till request success) or list of OutSchema
    _data, // list of OutSchema (initially empty)
    _dataMap, // map of OutSchema where key is id and value is OutSchema
    _instance, // OutSchema, first element of query.data or empty object
    create, // create mutation object
    update, // update mutation object
    _delete, // delete mutation object
  };
};

export const useElementsPagination = (
  initialPage = 1,
  initialOption = defaultElementsPerPageOptions[0]
) => {
  const [elementsPerPage, setElementsPerPage] = React.useState(initialOption);
  const [page, setPage] = React.useState(initialPage);
  return { elementsPerPage, setElementsPerPage, page, setPage };
};
